<template>
  <v-container class="contact-page" fluid>
    <v-popup-loading :isLoading="isPageLoading" />
    <div class="head-wrap mb-4">
      <div>
        <h1 class="headline mb-1">{{ isEditMode ? 'Edit ESG Activity' : 'Create ESG Activity' }}</h1>
        <p class="subhead mb-0">Fields marked <span class="required">*</span> are required.</p>
      </div>
      <div class="top-actions">
        <v-btn outlined class="mr-2" @click="onCancel">Cancel</v-btn>
        <v-btn v-if="canSave" color="primary" :loading="isSaving" :disabled="!isFormValid" @click="onSave">Save</v-btn>
      </div>
    </div>

    <v-card class="form-card">
      <div class="form-card-head">Content</div>
      <v-card-text class="pt-4">
        <v-row>
          <v-col cols="12" md="6"><label>ACTIVITY TITLE <span class="lang">TH</span> <span class="required">*</span></label><v-text-field dense outlined hide-details v-model="form.title_th" placeholder="เช่น ค่ายสุขภาพชุมชน 2569"/></v-col>
          <v-col cols="12" md="6"><label>ACTIVITY TITLE <span class="lang">EN</span> <span class="required">*</span></label><v-text-field dense outlined hide-details v-model="form.title_en" placeholder="e.g. Community Health Camp 2026"/></v-col>
          <v-col cols="12" md="6"><label>DESCRIPTION <span class="lang">TH</span></label><v-textarea dense outlined hide-details rows="3" v-model="form.description_th" placeholder="รายละเอียดกิจกรรม..."/></v-col>
          <v-col cols="12" md="6"><label>DESCRIPTION <span class="lang">EN</span></label><v-textarea dense outlined hide-details rows="3" v-model="form.description_en" placeholder="Activity description..."/></v-col>
          <v-col cols="12" md="6">
            <label>EVENT DATE <span class="required">*</span></label>
            <v-menu
              v-model="eventDateMenu"
              :close-on-content-click="false"
              offset-y
              min-width="auto"
              transition="scale-transition"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="form.event_date"
                  dense
                  outlined
                  hide-details
                  readonly
                  placeholder="YYYY-MM-DD"
                  prepend-inner-icon="mdi-calendar"
                  v-bind="attrs"
                  v-on="on"
                />
              </template>
              <v-date-picker v-model="form.event_date" @input="eventDateMenu = false" />
            </v-menu>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card class="form-card">
      <div class="form-card-head">Media</div>
      <v-card-text class="pt-4">
        <label class="mb-2 d-block">BANNER IMAGE</label>
        <input ref="bannerInput" type="file" class="d-none" accept="image/*" @change="onBannerChange" />
        <div class="upload-box" @click="openBannerPicker" @dragover.prevent @drop.prevent="onDropBanner">
          <v-icon class="upload-icon" size="26">mdi-cloud-upload-outline</v-icon>
          Drop banner image here or <b>Browse</b><br/><b>Upload to OBS</b>, Max file size 5 MB
        </div>
        <div v-if="form.banner_url" class="image-preview mt-3">
          <img :src="form.banner_url" alt="esg-banner" @error="onImageError" />
          <button type="button" class="thumb-remove" @click="removeBanner">×</button>
        </div>
      </v-card-text>
    </v-card>

    <v-card class="form-card actions-card">
      <v-card-actions class="modal-foot">
        <v-spacer />
        <v-btn outlined @click="onCancel">Cancel</v-btn>
        <v-btn v-if="canSave" color="primary" :loading="isSaving" :disabled="!isFormValid" @click="onSave">Save Activity</v-btn>
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<script>
import {mapActions, mapState} from 'vuex'
import {hasPermision} from '@/helper/helper'
import PopupLoading from '@/components/PopupLoading'

const defaultForm = () => ({
  id: null,
  title_th: '',
  title_en: '',
  description_th: '',
  description_en: '',
  event_date: '',
  banner_url: ''
})

const MAX_BANNER_SIZE_BYTES = 5 * 1024 * 1024 //5MB
const ESG_UPLOAD_FOLDER = 'corporate-content/esg-activities'

export default {
  components: {
    'v-popup-loading': PopupLoading
  },
  data() {
    return { form: defaultForm(), initialLoading: false, eventDateMenu: false }
  },
  computed: {
    ...mapState('esgActivities', ['list', 'isSaving']),
    isPageLoading() { return this.initialLoading },
    isEditMode() { return !!this.$route.params.id },
    canSave() { return this.isEditMode ? hasPermision('CanEditEsg') : hasPermision('CanAddEsg') },
    isFormValid() { return !!(this.form.title_th && this.form.title_en && this.form.event_date) }
  },
  async created() {
    this.initialLoading = true
    try {
      this.setResource('esg')
      await this.fetch()
      if (this.isEditMode) {
        const target = this.list.find(item => String(item.id) === String(this.$route.params.id))
        if (target) {
          this.form = {
            ...defaultForm(),
            ...target,
            event_date: this.normalizeDate(target.event_date)
          }
        }
      }
    } finally {
      this.initialLoading = false
    }
  },
  methods: {
    ...mapActions('esgActivities', ['setResource', 'fetch', 'save', 'upload']),
    normalizeDate(value) {
      if (!value) return ''
      const stringValue = String(value)
      if (/^\d{4}-\d{2}-\d{2}$/.test(stringValue)) return stringValue
      const date = new Date(stringValue)
      if (Number.isNaN(date.getTime())) return ''
      return date.toISOString().slice(0, 10)
    },
    onCancel() { this.$router.push({name: 'esg-list'}) },
    openBannerPicker() {
      this.$refs.bannerInput && this.$refs.bannerInput.click()
    },
    fileToDataUrl(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result)
        reader.onerror = reject
        reader.readAsDataURL(file)
      })
    },
    async uploadImage(file, namePrefix) {
      const base64 = await this.fileToDataUrl(file)
      const uploadName = `${namePrefix}_${Date.now()}`
      return await this.upload({
        name: uploadName,
        base64,
        folder: ESG_UPLOAD_FOLDER
      })
    },
    async assignBanner(file) {
      if (!file || !file.type.startsWith('image/')) return
      if (file.size > MAX_BANNER_SIZE_BYTES) return
      const imageUrl = await this.uploadImage(file, 'esg_banner')
      if (!imageUrl) return
      this.form.banner_url = imageUrl
    },
    removeBanner() {
      this.form.banner_url = ''
    },
    onImageError(event) {
      if (event && event.target) {
        event.target.style.display = 'none'
      }
    },
    async onBannerChange(event) {
      const file = event.target.files && event.target.files[0]
      await this.assignBanner(file)
      event.target.value = null
    },
    async onDropBanner(event) {
      const file = event.dataTransfer.files && event.dataTransfer.files[0]
      await this.assignBanner(file)
    },
    async onSave() {
      const result = await this.$store.dispatch('esgActivities/save', this.form)
      if (!result.ok) return
      this.$router.push({name: 'esg-list'})
    }
  }
}
</script>

<style scoped>
.contact-page { background: #f7f9fc; min-height: calc(100vh - 120px); }
.head-wrap { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
.headline { font-size: 30px !important; color: #0f1f3b; }
.subhead { color: #64748b; font-size: 13px; }
.top-actions { display: flex; align-items: center; }
.form-card { border: 1px solid #e6e9f0; border-radius: 10px; margin-bottom: 14px; background: #fff; overflow: hidden; }
.form-card-head { padding: 12px 14px; border-bottom: 1px solid #edf2fb; font-size: 16px; font-weight: 700; color: #0f1f3b; }
.modal-foot { border-top: 1px solid #edf2fb; padding: 10px 14px; }
.upload-box { border: 1.5px dashed #d9e5f8; border-radius: 10px; color: #7a8cad; padding: 28px; text-align: center; font-size: 13px; cursor: pointer; }
.upload-icon { display: block; margin: 0 auto 8px; color: #9ab0d6; }
.image-preview { width: 260px; height: 140px; border-radius: 8px; border: 1px solid #e5ebf6; overflow: hidden; position: relative; background: #fff; }
.image-preview img { width: 100%; height: 100%; object-fit: cover; }
.thumb-remove { position: absolute; top: 6px; right: 6px; width: 22px; height: 22px; border-radius: 50%; border: 1px solid #f2bcbc; background: #fff; color: #c62828; line-height: 18px; font-size: 14px; cursor: pointer; }
.required { color: #c62828; }
.lang { color: #2962ff; }
label { display:block; font-size:10.5px; color:#64748b; text-transform:uppercase; font-weight:700; margin-bottom:5px; letter-spacing:.4px; }
.actions-card { margin-bottom: 0; }
</style>
